/**
 * ZoomLogi Simulator scenarios chosen while creating a shipment.
 * Contract: eng-wiki projects/synthetic-providers/docs/stage2-scenarios.md.
 * The limits mirror the backend (shipment/services/synthetic_providers.py),
 * which validates again; checking here keeps the user from submitting a
 * scenario the backend would reject.
 */

import type { SimulatorAlertThreshold } from '../../apis/shipment';
import type { SensorFormRow } from './sensorFormPayload';

export type ScenarioType =
  | 'default'
  | 'temperature_excursion'
  | 'humidity_excursion'
  | 'light_exposure'
  | 'tilt_exceeded'
  | 'pressure_exceeded'
  | 'shock_exceeded'
  | 'temperature_nearing_limit'
  | 'low_battery'
  | 'shipment_not_moving'
  | 'low_battery_at_origin'
  | 'pickup_overdue'
  | 'driver_cant_find_location'
  | 'incorrect_delivery_location'
  | 'delivery_overdue'
  | 'viability_window_warning'
  | 'viability_at_risk_before_arrival'
  | 'viability_window_exhausted';
/** What a scenario measures; its alarm needs a product with limits for it. */
export type ScenarioMetric =
  | 'temperature'
  | 'humidity'
  | 'light'
  | 'tilt'
  | 'pressure'
  | 'shock'
  | 'viability';
export type ExcursionDirection = 'high' | 'low';
type MagnitudeKey =
  | 'magnitude_c'
  | 'magnitude_pct'
  | 'magnitude_lux'
  | 'magnitude_deg'
  | 'magnitude_psi'
  | 'magnitude_g';

export interface ExcursionConfig {
  direction: ExcursionDirection;
  /** Degrees beyond the limit (temperature excursion). */
  magnitude_c: number;
  /** Percentage points beyond the limit (humidity excursion). */
  magnitude_pct: number;
  /** Lux beyond the limit (light exposure). */
  magnitude_lux: number;
  /** Degrees of tilt beyond the limit. */
  magnitude_deg: number;
  /** Psi beyond the limit (pressure). */
  magnitude_psi: number;
  /** G beyond the limit (shock). */
  magnitude_g: number;
  start_offset_min: number;
  duration_min: number;
}

/** A product picked in the scenario builder; its limits drive the thresholds. */
export interface ScenarioProduct {
  id: string;
  name: string;
  temperature_min: number | null;
  temperature_max: number | null;
  humidity_min: number | null;
  humidity_max: number | null;
  /** Maximums for light (lux), tilt (degrees) and shock (G); a range for pressure (psi). */
  light?: number | null;
  tilt?: number | null;
  shock?: number | null;
  pressure_min?: number | null;
  pressure_max?: number | null;
  /** The product's viability window, in hours. */
  viability_window?: number | null;
}

export interface SimulationScenarioConfig {
  scenario_type: ScenarioType;
  product: ScenarioProduct | null;
  journey_minutes: number;
  /** 0 starts now; anything else starts that long ago. */
  started_minutes_ago: number;
  /** Scenario clock speed; only applies to a start in the past. */
  pace: number;
  temperature_min: number;
  temperature_max: number;
  excursion: ExcursionConfig;
  /** Temperature nearing limit: how close to an edge, as % of the range. */
  margin_pct: number;
  /** Low battery: the level the battery falls below. */
  battery_threshold_pct: number;
  /** Low battery: minutes after pickup that it crosses the threshold. */
  battery_low_offset_min: number;
  /** Incorrect delivery location: how far from the destination it lands, in feet. */
  delivery_offset_ft: number;
}

/** The company's alert settings by scenario type, from the backend. */
export type AlertThresholds = Record<string, SimulatorAlertThreshold>;

export const MIN_JOURNEY_MINUTES = 60;
export const MAX_JOURNEY_MINUTES = 4320;
export const MAX_PACE = 60;
export const MAX_STARTED_MINUTES_AGO = 7 * 24 * 60;
/** The alarm engine looks back about 2 days; warn well before that. */
export const WARN_STARTED_MINUTES_AGO = 36 * 60;
export const MIN_EXCURSION_MINUTES = 5;
/** The idle alert needs at least three GPS fixes, 5 minutes apart. */
export const MIN_IDLE_MINUTES = 15;
export const MAX_EXCURSION_MAGNITUDE_C = 30;
export const MAX_EXCURSION_MAGNITUDE_PCT = 50;
export const MAX_NEARING_MARGIN_PCT = 50;
export const MAX_BATTERY_THRESHOLD_PCT = 95;
export const MIN_ORIGIN_BATTERY_THRESHOLD_PCT = 5;
/** 100 m to 100 km, the range the backend accepts, in feet. */
export const MIN_DELIVERY_OFFSET_FT = 330;
export const MAX_DELIVERY_OFFSET_FT = 328000;
const METRES_PER_FOOT = 0.3048;
/** The simulator adds up to this much jitter to an excursion reading. */
const EXCURSION_JITTER = 0.2;
/** Polls are 60 s apart and the alarm engine sees only the newest reading. */
export const MIN_EXCURSION_WALL_MINUTES = 2;
/** Pickup is 10 of the default 120 journey minutes and scales with the journey. */
const PICKUP_SHARE_OF_JOURNEY = 10 / 120;
/** A duration alert fires on a later reading, one 5-minute step past its threshold. */
const READING_STEP_MINUTES = 5;
/** The idle alert looks back over its threshold and needs three fixes in it. */
const IDLE_EXTRA_MINUTES = 10;
/** Back-dated scenarios release their scans this much faster than real time. */
const BACKDATED_PACE = 60;
/** Slack so a threshold is passed rather than met, and a journey is still running. */
const SCHEDULE_MARGIN_MINUTES = 15;
const BEFORE_DELIVERY_MINUTES = 15;
/** The "Out for delivery" scan, as a share of the journey. */
const OUT_FOR_DELIVERY_SHARE_OF_JOURNEY = 95 / 120;

export const JOURNEY_OPTIONS = [
  { label: '2 hours', value: 120 },
  { label: '6 hours', value: 360 },
  { label: '12 hours', value: 720 },
  { label: '1 day', value: 1440 },
  { label: '2 days', value: 2880 },
  { label: '3 days', value: 4320 },
];

export const PACE_OPTIONS = [
  { label: 'Normal (1x)', value: 1 },
  { label: 'Accelerated 2x', value: 2 },
  { label: 'Accelerated 4x', value: 4 },
  { label: 'Accelerated 8x', value: 8 },
  { label: 'Accelerated 12x', value: 12 },
  { label: 'Accelerated 24x', value: 24 },
  { label: 'Accelerated 60x', value: 60 },
];

interface ScenarioTypeOption {
  label: string;
  value?: ScenarioType;
  options?: { label: string; value: ScenarioType }[];
}

/** Alerts are grouped by when they apply, as in alert settings. */
export const SCENARIO_TYPE_OPTIONS: ScenarioTypeOption[] = [
  { label: 'Standard on-time journey', value: 'default' },
  {
    label: 'Pre-pick',
    options: [
      { label: 'Low battery at origin', value: 'low_battery_at_origin' },
      { label: 'Pickup overdue', value: 'pickup_overdue' },
    ],
  },
  {
    label: 'At delivery',
    options: [
      {
        label: "Driver can't find location",
        value: 'driver_cant_find_location',
      },
      {
        label: 'Incorrect delivery location',
        value: 'incorrect_delivery_location',
      },
      { label: 'Delivery overdue', value: 'delivery_overdue' },
    ],
  },
  {
    label: 'Continuous monitoring',
    options: [
      { label: 'Viability window warning', value: 'viability_window_warning' },
      {
        label: 'Viability at risk before arrival',
        value: 'viability_at_risk_before_arrival',
      },
      {
        label: 'Viability window expired',
        value: 'viability_window_exhausted',
      },
      { label: 'Low battery', value: 'low_battery' },
      { label: 'Shipment not moving', value: 'shipment_not_moving' },
      {
        label: 'Temperature nearing limit',
        value: 'temperature_nearing_limit',
      },
      { label: 'Temperature excursion', value: 'temperature_excursion' },
      { label: 'Light exposure', value: 'light_exposure' },
      { label: 'Tilt threshold exceeded', value: 'tilt_exceeded' },
      { label: 'Pressure range exceeded', value: 'pressure_exceeded' },
      { label: 'Humidity excursion', value: 'humidity_excursion' },
      { label: 'Shock threshold exceeded', value: 'shock_exceeded' },
    ],
  },
];

export const DEFAULT_SCENARIO: SimulationScenarioConfig = {
  scenario_type: 'temperature_excursion',
  product: null,
  journey_minutes: 120,
  started_minutes_ago: 0,
  pace: 1,
  temperature_min: 2,
  temperature_max: 8,
  excursion: {
    direction: 'high',
    magnitude_c: 5,
    magnitude_pct: 15,
    magnitude_lux: 100,
    magnitude_deg: 15,
    magnitude_psi: 2,
    magnitude_g: 3,
    start_offset_min: 30,
    duration_min: 20,
  },
  margin_pct: 10,
  battery_threshold_pct: 20,
  battery_low_offset_min: 30,
  delivery_offset_ft: 2000,
};

const toLimit = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** The product as the scenario builder keeps it (temperature in Celsius, humidity in %). */
export const toScenarioProduct = (product: {
  id: string;
  name: string;
  temperature_min?: string | number | null;
  temperature_max?: string | number | null;
  humidity_min?: string | number | null;
  humidity_max?: string | number | null;
  light?: string | number | null;
  tilt?: string | number | null;
  shock?: string | number | null;
  pressure_min?: string | number | null;
  pressure_max?: string | number | null;
  viability_window?: string | number | null;
}): ScenarioProduct => ({
  id: product.id,
  name: product.name,
  temperature_min: toLimit(product.temperature_min),
  temperature_max: toLimit(product.temperature_max),
  humidity_min: toLimit(product.humidity_min),
  humidity_max: toLimit(product.humidity_max),
  light: toLimit(product.light),
  tilt: toLimit(product.tilt),
  shock: toLimit(product.shock),
  pressure_min: toLimit(product.pressure_min),
  pressure_max: toLimit(product.pressure_max),
  viability_window: toLimit(product.viability_window),
});

interface MetricInfo {
  name: string;
  unit: string;
  /** The excursion field holding the size beyond the limit. */
  magnitudeKey: MagnitudeKey;
  magnitudeLabel: string;
  /** What the size is counted in, in a sentence: "5 degrees", "15 percentage points". */
  magnitudeNoun: string;
  maxMagnitude: number;
  /** The highest a reading can go (humidity is 0-100%, tilt 0-180°). */
  ceiling: number | null;
  /** Readings cannot go below 0 for any metric but temperature. */
  floor: number | null;
  /** Light, tilt and shock only have a maximum. */
  maxOnly: boolean;
  directionLabels: Record<ExcursionDirection, string>;
}

export const METRIC_INFO: Record<ScenarioMetric, MetricInfo> = {
  temperature: {
    name: 'Temperature',
    unit: '°C',
    magnitudeKey: 'magnitude_c',
    magnitudeLabel: 'Degrees beyond limit',
    magnitudeNoun: 'degrees',
    maxMagnitude: MAX_EXCURSION_MAGNITUDE_C,
    ceiling: null,
    floor: null,
    maxOnly: false,
    directionLabels: {
      high: 'Too warm (above the maximum)',
      low: 'Too cold (below the minimum)',
    },
  },
  humidity: {
    name: 'Humidity',
    unit: '%',
    magnitudeKey: 'magnitude_pct',
    magnitudeLabel: 'Percent beyond limit',
    magnitudeNoun: 'percentage points',
    maxMagnitude: MAX_EXCURSION_MAGNITUDE_PCT,
    ceiling: 100,
    floor: 0,
    maxOnly: false,
    directionLabels: {
      high: 'Too humid (above the maximum)',
      low: 'Too dry (below the minimum)',
    },
  },
  light: {
    name: 'Light',
    unit: 'lux',
    magnitudeKey: 'magnitude_lux',
    magnitudeLabel: 'Lux beyond limit',
    magnitudeNoun: 'lux',
    maxMagnitude: 10000,
    ceiling: null,
    floor: 0,
    maxOnly: true,
    directionLabels: { high: 'Too bright (above the maximum)', low: '' },
  },
  tilt: {
    name: 'Tilt',
    unit: '°',
    magnitudeKey: 'magnitude_deg',
    magnitudeLabel: 'Degrees beyond limit',
    magnitudeNoun: 'degrees',
    maxMagnitude: 90,
    ceiling: 180,
    floor: 0,
    maxOnly: true,
    directionLabels: { high: 'Tilted (above the maximum)', low: '' },
  },
  pressure: {
    name: 'Pressure',
    unit: 'psi',
    magnitudeKey: 'magnitude_psi',
    magnitudeLabel: 'Psi beyond limit',
    magnitudeNoun: 'psi',
    maxMagnitude: 100,
    ceiling: null,
    floor: 0,
    maxOnly: false,
    directionLabels: {
      high: 'Too high (above the maximum)',
      low: 'Too low (below the minimum)',
    },
  },
  viability: {
    name: 'Viability window',
    unit: 'h',
    // A viability scenario has no excursion size; these are never read.
    magnitudeKey: 'magnitude_c',
    magnitudeLabel: '',
    magnitudeNoun: 'hours',
    maxMagnitude: 0,
    ceiling: null,
    floor: null,
    maxOnly: true,
    directionLabels: { high: '', low: '' },
  },
  shock: {
    name: 'Shock',
    unit: 'G',
    magnitudeKey: 'magnitude_g',
    magnitudeLabel: 'G beyond limit',
    magnitudeNoun: 'G',
    maxMagnitude: 100,
    ceiling: null,
    floor: 0,
    maxOnly: true,
    directionLabels: { high: 'Impact (above the maximum)', low: '' },
  },
};

/** The directions a metric's excursion can go. */
export const metricDirections = (
  metric: ScenarioMetric
): ExcursionDirection[] =>
  METRIC_INFO[metric].maxOnly ? ['high'] : ['high', 'low'];

/** What a scenario measures; null when it needs no product limits. */
const SCENARIO_METRIC: Record<ScenarioType, ScenarioMetric | null> = {
  default: null,
  temperature_excursion: 'temperature',
  humidity_excursion: 'humidity',
  light_exposure: 'light',
  tilt_exceeded: 'tilt',
  pressure_exceeded: 'pressure',
  shock_exceeded: 'shock',
  temperature_nearing_limit: 'temperature',
  low_battery: null,
  shipment_not_moving: null,
  low_battery_at_origin: null,
  pickup_overdue: null,
  driver_cant_find_location: null,
  incorrect_delivery_location: null,
  delivery_overdue: null,
  viability_window_warning: 'viability',
  viability_at_risk_before_arrival: 'viability',
  viability_window_exhausted: 'viability',
};

export const VIABILITY_TYPES: ScenarioType[] = [
  'viability_window_warning',
  'viability_at_risk_before_arrival',
  'viability_window_exhausted',
];
export const isViability = (type: ScenarioType): boolean =>
  VIABILITY_TYPES.includes(type);
/** Judged against the wall clock, so the start is set back to put "now" in the right place. */
const BACKDATED_TYPES: ScenarioType[] = [
  'pickup_overdue',
  'delivery_overdue',
  'viability_window_warning',
  'viability_window_exhausted',
];
export const isBackdated = (type: ScenarioType): boolean =>
  BACKDATED_TYPES.includes(type);
/** Scenarios that start from a battery level rather than a product limit. */
export const hasBatteryThreshold = (type: ScenarioType): boolean =>
  type === 'low_battery' || type === 'low_battery_at_origin';

export const scenarioMetric = (type: ScenarioType): ScenarioMetric | null =>
  SCENARIO_METRIC[type];

/** The scenarios that push a reading beyond a product limit by a chosen size. */
export const hasMagnitude = (type: ScenarioType): boolean =>
  SCENARIO_METRIC[type] !== null &&
  type !== 'temperature_nearing_limit' &&
  !isViability(type);
/** Only some scenarios have an incident window: a stretch of readings or positions. */
const WINDOW_TYPES: ScenarioType[] = [
  'temperature_excursion',
  'humidity_excursion',
  'light_exposure',
  'tilt_exceeded',
  'pressure_exceeded',
  'shock_exceeded',
  'temperature_nearing_limit',
  'shipment_not_moving',
  'driver_cant_find_location',
];
export const hasWindow = (type: ScenarioType): boolean =>
  WINDOW_TYPES.includes(type);
/** Nearing limit also picks a side of the range to hug. */
export const hasDirection = (type: ScenarioType): boolean => {
  const metric = SCENARIO_METRIC[type];
  return (
    metric !== null &&
    (hasMagnitude(type) || type === 'temperature_nearing_limit') &&
    !METRIC_INFO[metric].maxOnly
  );
};
const windowMinimum = (type: ScenarioType): number =>
  type === 'shipment_not_moving' || type === 'driver_cant_find_location'
    ? MIN_IDLE_MINUTES
    : MIN_EXCURSION_MINUTES;

const PRODUCT_LIMITS: Record<
  ScenarioMetric,
  (product: ScenarioProduct) => { min: number | null; max: number | null }
> = {
  temperature: (p) => ({ min: p.temperature_min, max: p.temperature_max }),
  humidity: (p) => ({ min: p.humidity_min, max: p.humidity_max }),
  pressure: (p) => ({
    min: p.pressure_min ?? null,
    max: p.pressure_max ?? null,
  }),
  // A maximum of 0 or less is no limit; the minimum is where the scale starts.
  light: (p) => ({ min: 0, max: p.light && p.light > 0 ? p.light : null }),
  tilt: (p) => ({ min: 0, max: p.tilt && p.tilt > 0 ? p.tilt : null }),
  shock: (p) => ({ min: 0, max: p.shock && p.shock > 0 ? p.shock : null }),
  viability: (p) => ({
    min: 0,
    max:
      p.viability_window && p.viability_window > 0 ? p.viability_window : null,
  }),
};

/** The product's limits for a metric, or null when it has none. */
export const productLimits = (
  product: ScenarioProduct | null,
  metric: ScenarioMetric | null
): { min: number; max: number } | null => {
  if (product === null || metric === null) {
    return null;
  }
  const { min, max } = PRODUCT_LIMITS[metric](product);
  return min !== null && max !== null ? { min, max } : null;
};

export const METRIC_UNIT: Record<ScenarioMetric, string> = {
  temperature: METRIC_INFO.temperature.unit,
  humidity: METRIC_INFO.humidity.unit,
  light: METRIC_INFO.light.unit,
  tilt: METRIC_INFO.tilt.unit,
  pressure: METRIC_INFO.pressure.unit,
  shock: METRIC_INFO.shock.unit,
  viability: METRIC_INFO.viability.unit,
};

/** A reading or limit with its unit: "8 °C", "50%", "30°". */
export const formatMetricValue = (
  value: number | string,
  metric: ScenarioMetric
): string =>
  metric === 'humidity' || metric === 'tilt'
    ? `${value}${METRIC_UNIT[metric]}`
    : `${value} ${METRIC_UNIT[metric]}`;

/** A range with its unit once ("2 to 8 °C"); a maximum on its own is "up to 100 lux". */
const formatMetricRange = (
  limits: { min: number; max: number },
  metric: ScenarioMetric
): string => {
  if (metric === 'viability') {
    return `${limits.max}h`;
  }
  return METRIC_INFO[metric].maxOnly
    ? `up to ${formatMetricValue(limits.max, metric)}`
    : `${limits.min} to ${formatMetricValue(limits.max, metric)}`;
};

/** "Temperature 2 to 8 °C": the product's limits for the metric, under its name. */
export const formatProductLimits = (
  product: ScenarioProduct,
  metric: ScenarioMetric
): string => {
  const limits = productLimits(product, metric);
  return limits
    ? `${METRIC_INFO[metric].name} ${formatMetricRange(limits, metric)}`
    : '';
};

/** Minutes between pickup and delivery for a journey. */
export const inTransitMinutes = (journeyMinutes: number): number =>
  Math.floor(journeyMinutes - journeyMinutes * PICKUP_SHARE_OF_JOURNEY);

export const pickupMinutes = (journeyMinutes: number): number =>
  journeyMinutes * PICKUP_SHARE_OF_JOURNEY;

/** The pace that actually applies: a scenario starting now always runs live. */
export const effectivePace = (config: SimulationScenarioConfig): number =>
  config.started_minutes_ago > 0 ? Math.max(config.pace, 1) : 1;

/**
 * Minutes of real time until the scenario clock reaches `virtualMinutes` after
 * the start. The clock runs `pace` times faster but never passes the present,
 * so after catching up it runs live.
 */
export const wallMinutesAt = (
  virtualMinutes: number,
  startedMinutesAgo: number,
  pace: number
): number => {
  if (startedMinutesAgo <= 0 || pace <= 1) {
    return virtualMinutes;
  }
  const catchUpAfter = startedMinutesAgo / (pace - 1);
  const direct = virtualMinutes / pace;
  return direct <= catchUpAfter ? direct : virtualMinutes - startedMinutesAgo;
};

/** The problems that stop the scenario from being created. */
export const validateScenario = (
  config: SimulationScenarioConfig
): string[] => {
  const errors: string[] = [];
  if (
    config.journey_minutes < MIN_JOURNEY_MINUTES ||
    config.journey_minutes > MAX_JOURNEY_MINUTES
  ) {
    errors.push('Choose a transit length from 1 hour to 3 days.');
  }
  if (
    config.started_minutes_ago < 0 ||
    config.started_minutes_ago > MAX_STARTED_MINUTES_AGO
  ) {
    errors.push('The start can be now or up to 7 days ago.');
  }
  if (config.pace < 1 || config.pace > MAX_PACE) {
    errors.push(`The pace must be between 1x and ${MAX_PACE}x.`);
  }
  const type = config.scenario_type;
  const available = inTransitMinutes(config.journey_minutes);
  if (type === 'low_battery_at_origin') {
    if (
      config.battery_threshold_pct < MIN_ORIGIN_BATTERY_THRESHOLD_PCT ||
      config.battery_threshold_pct > MAX_BATTERY_THRESHOLD_PCT
    ) {
      errors.push(
        `The battery threshold must be between ${MIN_ORIGIN_BATTERY_THRESHOLD_PCT}% and ${MAX_BATTERY_THRESHOLD_PCT}%.`
      );
    }
    return errors;
  }
  if (type === 'incorrect_delivery_location') {
    if (
      !(config.delivery_offset_ft >= MIN_DELIVERY_OFFSET_FT) ||
      config.delivery_offset_ft > MAX_DELIVERY_OFFSET_FT
    ) {
      errors.push(
        `The delivery must land between ${MIN_DELIVERY_OFFSET_FT} and ${MAX_DELIVERY_OFFSET_FT} feet from the destination.`
      );
    }
    return errors;
  }
  if (type === 'low_battery') {
    if (
      !(config.battery_threshold_pct > 0) ||
      config.battery_threshold_pct > MAX_BATTERY_THRESHOLD_PCT
    ) {
      errors.push(
        `The battery threshold must be above 0 and at most ${MAX_BATTERY_THRESHOLD_PCT}%.`
      );
    }
    if (
      config.battery_low_offset_min < 0 ||
      config.battery_low_offset_min > available
    ) {
      errors.push(
        `The battery must fall below the threshold before delivery: at most ${available} minutes after pickup.`
      );
    }
    return errors;
  }
  const metric = scenarioMetric(type);
  if (metric !== null) {
    // Limits are never typed in: they come from the product.
    if (productLimits(config.product, metric) === null) {
      errors.push(`Choose a product with ${metric} limits.`);
    }
  }
  if (isViability(type)) {
    const hours = config.product?.viability_window;
    if (hours && minimumViabilityJourney(hours * 60) > MAX_JOURNEY_MINUTES) {
      errors.push(
        "This product's viability window is too long to play: the longest transit is 3 days."
      );
    }
    if (
      config.started_minutes_ago + BEFORE_DELIVERY_MINUTES >
      config.journey_minutes
    ) {
      errors.push(
        'The transit must still be running: make it longer than the time since the start.'
      );
    }
  }
  if (!hasWindow(type)) {
    return errors;
  }
  const { excursion } = config;
  if (metric !== null && hasMagnitude(type)) {
    const info = METRIC_INFO[metric];
    const range = productLimits(config.product, metric);
    const magnitude = excursion[info.magnitudeKey];
    if (!(magnitude > 0) || magnitude > info.maxMagnitude) {
      errors.push(
        `The excursion must be above 0 and at most ${info.maxMagnitude} ${
          info.magnitudeNoun
        } beyond the limit.`
      );
    } else if (range !== null) {
      if (
        excursion.direction === 'high' &&
        info.ceiling !== null &&
        range.max + magnitude + EXCURSION_JITTER > info.ceiling
      ) {
        errors.push(
          `The excursion would go above ${formatMetricValue(info.ceiling, metric)} ${info.name.toLowerCase()}. Lower its size.`
        );
      }
      if (
        excursion.direction === 'low' &&
        info.floor !== null &&
        range.min - magnitude - EXCURSION_JITTER < info.floor
      ) {
        errors.push(
          `The excursion would go below ${formatMetricValue(info.floor, metric)} ${info.name.toLowerCase()}. Lower its size.`
        );
      }
    }
  }
  if (type === 'temperature_nearing_limit') {
    if (
      !(config.margin_pct > 0) ||
      config.margin_pct > MAX_NEARING_MARGIN_PCT
    ) {
      errors.push(
        `The margin must be above 0 and at most ${MAX_NEARING_MARGIN_PCT}% of the temperature range.`
      );
    }
  }
  if (excursion.start_offset_min < 0) {
    errors.push('The excursion cannot start before pickup.');
  }
  if (excursion.duration_min < windowMinimum(type)) {
    errors.push(
      `The excursion must last at least ${windowMinimum(type)} minutes.`
    );
  }
  if (excursion.start_offset_min + excursion.duration_min > available) {
    errors.push(
      `The excursion must end before delivery: its start plus its duration can be at most ${available} minutes after pickup.`
    );
  }
  if (
    excursion.duration_min / effectivePace(config) <
    MIN_EXCURSION_WALL_MINUTES
  ) {
    errors.push(
      `At ${effectivePace(config)}x the excursion would last under ${MIN_EXCURSION_WALL_MINUTES} minutes of real time and may be missed. Lengthen it or lower the pace.`
    );
  }
  return errors;
};

/** Things worth knowing that do not stop the scenario. */
export const scenarioWarnings = (
  config: SimulationScenarioConfig
): string[] => {
  const warnings: string[] = [];
  // These alerts are judged on the wall clock, so setting the start back is the point.
  if (isBackdated(config.scenario_type)) {
    return warnings;
  }
  if (config.started_minutes_ago > WARN_STARTED_MINUTES_AGO) {
    warnings.push(
      'The alarm service only looks back about 2 days, and alerts dated in the past may not send notifications. Start more recently for a reliable alert.'
    );
  } else if (config.started_minutes_ago > 0) {
    warnings.push(
      'Events keep their past timestamps, so alerts may not perform as expected.'
    );
  }
  return warnings;
};

/** The scenarios whose alert waits a number of minutes beyond the limit. */
const DURATION_ALERTS: ScenarioType[] = [
  'temperature_excursion',
  'humidity_excursion',
  'light_exposure',
  'tilt_exceeded',
  'pressure_exceeded',
];

/** The alert's setting when it is on and has a number, otherwise null. */
const alertSetting = (
  type: ScenarioType,
  thresholds: AlertThresholds | null | undefined
): number | null => {
  const entry = thresholds?.[type];
  return entry?.enabled && entry.threshold !== null ? entry.threshold : null;
};

/**
 * The setting an alert is judged against when the company's own is not known:
 * the seeded default of each alert whose timing the scenario depends on.
 */
const SEED_SETTING: Partial<Record<ScenarioType, number>> = {
  pickup_overdue: 30,
  delivery_overdue: 48 * 60,
  viability_window_warning: 120,
  viability_at_risk_before_arrival: 60,
  viability_window_exhausted: 0,
};

/** The shortest incident window that makes the alert fire, or null when it needs none. */
const requiredDuration = (
  type: ScenarioType,
  setting: number | null
): number | null => {
  if (setting === null) {
    return null;
  }
  if (DURATION_ALERTS.includes(type)) {
    return setting + READING_STEP_MINUTES;
  }
  return type === 'shipment_not_moving' || type === 'driver_cant_find_location'
    ? setting + IDLE_EXTRA_MINUTES
    : null;
};

const roundUpTo5 = (minutes: number): number => Math.ceil(minutes / 5) * 5;

/** The shortest journey that still has the product's viability window ahead of it. */
const minimumViabilityJourney = (windowMinutes: number): number =>
  roundUpTo5(
    (windowMinutes + BEFORE_DELIVERY_MINUTES + SCHEDULE_MARGIN_MINUTES) /
      (1 - PICKUP_SHARE_OF_JOURNEY)
  );

/**
 * When a viability scenario starts and how long it runs, so the product's
 * window (hours) is in the state the alert needs. The pickup is a twelfth of
 * the way in and the window runs from it; the shipment must still be on the
 * road, so the journey outlasts the time since the start. Null when the window
 * is too long for the longest journey.
 */
export const viabilityPlan = (
  type: ScenarioType,
  windowHours: number,
  setting: number
): {
  journey_minutes: number;
  started_minutes_ago: number;
  pace: number;
} | null => {
  const window = windowHours * 60;
  // Minutes between the start and "now" relative to the window's end.
  const behind: Partial<Record<ScenarioType, number>> = {
    // Inside the last part of the window, a quarter of the buffer clear of its end.
    viability_window_warning: -Math.max(setting * 0.75, 5),
    // Past the end by the buffer, with slack.
    viability_window_exhausted: setting + SCHEDULE_MARGIN_MINUTES,
  };
  const offset = behind[type];
  if (offset === undefined) {
    // At risk: live, with the journey set to arrive after the window ends.
    const journey = roundUpTo5((window + 30) / (1 - PICKUP_SHARE_OF_JOURNEY));
    return journey > MAX_JOURNEY_MINUTES
      ? null
      : { journey_minutes: journey, started_minutes_ago: 0, pace: 1 };
  }
  const journey = roundUpTo5(
    (window + offset + BEFORE_DELIVERY_MINUTES) / (1 - PICKUP_SHARE_OF_JOURNEY)
  );
  if (journey > MAX_JOURNEY_MINUTES) {
    return null;
  }
  return {
    journey_minutes: journey,
    started_minutes_ago: Math.round(
      journey * PICKUP_SHARE_OF_JOURNEY + window + offset
    ),
    pace: BACKDATED_PACE,
  };
};

/** The earliest start (minutes ago) that makes a wall-clock alert true, with no slack. */
const requiredStart = (
  config: SimulationScenarioConfig,
  setting: number
): number | null => {
  const journey = config.journey_minutes;
  if (config.scenario_type === 'pickup_overdue') {
    return pickupMinutes(journey) + setting;
  }
  if (config.scenario_type === 'delivery_overdue') {
    return Math.max(
      journey,
      journey * OUT_FOR_DELIVERY_SHARE_OF_JOURNEY + setting
    );
  }
  return null;
};

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high);

/**
 * The scenario sized to beat the company's alert settings: a duration alert gets
 * a window a step longer than its wait, the margin and battery level follow the
 * alert, the journey grows to fit the window, and the alerts judged on the wall
 * clock get a start set back far enough. Unchanged without a setting.
 */
export const sizeScenario = (
  config: SimulationScenarioConfig,
  thresholds: AlertThresholds | null | undefined
): SimulationScenarioConfig => {
  const type = config.scenario_type;
  const known = alertSetting(type, thresholds);
  const setting = known ?? SEED_SETTING[type] ?? null;
  if (setting === null) {
    return config;
  }
  let next = config;
  if (isViability(type)) {
    const hours = next.product?.viability_window;
    const plan = hours ? viabilityPlan(type, hours, setting) : null;
    return plan ? { ...next, ...plan } : next;
  }
  const start = requiredStart(next, setting);
  if (start !== null) {
    const slack =
      type === 'delivery_overdue'
        ? 2 * SCHEDULE_MARGIN_MINUTES
        : SCHEDULE_MARGIN_MINUTES;
    return {
      ...next,
      started_minutes_ago: Math.ceil(start + slack),
      pace: type === 'delivery_overdue' ? BACKDATED_PACE : next.pace,
    };
  }
  if (known === null) {
    return config;
  }
  if (type === 'temperature_nearing_limit' && setting > 0) {
    next = { ...next, margin_pct: Math.min(setting, MAX_NEARING_MARGIN_PCT) };
  }
  if (type === 'low_battery' && setting > 0) {
    next = {
      ...next,
      battery_threshold_pct: Math.min(setting, MAX_BATTERY_THRESHOLD_PCT),
    };
  }
  if (type === 'low_battery_at_origin' && setting > 0) {
    next = {
      ...next,
      battery_threshold_pct: clamp(
        setting,
        MIN_ORIGIN_BATTERY_THRESHOLD_PCT,
        MAX_BATTERY_THRESHOLD_PCT
      ),
    };
  }
  if (type === 'incorrect_delivery_location') {
    next = {
      ...next,
      delivery_offset_ft: clamp(
        Math.ceil(Math.max(2000, setting * 3)),
        MIN_DELIVERY_OFFSET_FT,
        MAX_DELIVERY_OFFSET_FT
      ),
    };
  }
  const required = requiredDuration(type, setting);
  if (required !== null) {
    const duration = Math.max(required, windowMinimum(type), 20);
    const needed = next.excursion.start_offset_min + duration;
    const fits = JOURNEY_OPTIONS.find(
      (option) => inTransitMinutes(option.value) >= needed
    );
    next = {
      ...next,
      journey_minutes: Math.max(
        next.journey_minutes,
        fits?.value ?? MAX_JOURNEY_MINUTES
      ),
      excursion: { ...next.excursion, duration_min: duration },
    };
  }
  return next;
};

/** What the company's alert settings mean for this scenario. */
export const thresholdWarnings = (
  config: SimulationScenarioConfig,
  thresholds: AlertThresholds | null | undefined
): string[] => {
  const type = config.scenario_type;
  const entry = thresholds?.[type];
  if (!entry) {
    return [];
  }
  if (!entry.enabled) {
    return [
      "This alert is off for the shipment's teams, so the scenario will not raise it.",
    ];
  }
  const setting = alertSetting(type, thresholds);
  if (setting === null) {
    return [];
  }
  const required = requiredDuration(type, setting);
  if (required !== null && config.excursion.duration_min < required) {
    return [
      `The alert waits ${formatMinutes(setting)}, so a window this short will not raise it.`,
    ];
  }
  const start = requiredStart(config, setting);
  if (start !== null && config.started_minutes_ago < start) {
    return [
      `The alert waits ${formatMinutes(setting)}: start at least ${formatMinutes(start)} ago for it to be overdue.`,
    ];
  }
  if (
    type === 'incorrect_delivery_location' &&
    config.delivery_offset_ft <= setting
  ) {
    return [
      `The alert allows ${setting} feet, so a delivery this close will not raise it.`,
    ];
  }
  if (type === 'viability_window_warning' && setting <= 0) {
    return [
      'This alert has no buffer before the window ends, so it will not raise.',
    ];
  }
  return [];
};

export const formatMinutes = (minutes: number): string => {
  const rounded = Math.max(Math.round(minutes), 0);
  if (rounded < 60) {
    return `${rounded}min`;
  }
  if (rounded < 2880) {
    const hours = rounded / 60;
    return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
  }
  const days = rounded / 1440;
  return `${Number.isInteger(days) ? days : days.toFixed(1)} days`;
};

/** What the scenario will do, in plain words. */
export const summarizeScenario = (
  config: SimulationScenarioConfig
): string[] => {
  const pace = effectivePace(config);
  const lines: string[] = [];
  const journey = formatMinutes(config.journey_minutes);
  if (config.started_minutes_ago <= 0) {
    lines.push(`A ${journey} journey starting now, played live.`);
  } else {
    const wall = wallMinutesAt(
      config.journey_minutes,
      config.started_minutes_ago,
      pace
    );
    lines.push(
      `A ${journey} journey that started ${formatMinutes(config.started_minutes_ago)} ago, ${
        pace > 1 ? `accelerated ${pace}x` : 'at normal pace'
      }: it arrives over about ${formatMinutes(wall)} with past timestamps.`
    );
  }
  const type = config.scenario_type;
  const metric = scenarioMetric(type);
  const { excursion } = config;
  const range = productLimits(config.product, metric);
  if (metric === 'viability' && config.product && range) {
    lines.push(
      `Product ${config.product.name} has a ${formatMetricRange(range, metric)} viability window.`
    );
  } else if (metric !== null && config.product && range) {
    lines.push(
      `Product ${config.product.name} sets the ${metric} ${
        METRIC_INFO[metric].maxOnly ? 'limit' : 'limits'
      } to ${formatMetricRange(range, metric)}.`
    );
  }
  if (metric !== null && range && hasMagnitude(type)) {
    const magnitude = excursion[METRIC_INFO[metric].magnitudeKey];
    const limit = excursion.direction === 'high' ? range.max : range.min;
    const reading =
      excursion.direction === 'high' ? limit + magnitude : limit - magnitude;
    lines.push(
      `Readings go about ${formatMetricValue(magnitude, metric)} ${
        excursion.direction === 'high' ? 'above' : 'below'
      } the ${formatMetricValue(limit, metric)} limit (around ${formatMetricValue(reading.toFixed(1), metric)}), starting ${
        excursion.start_offset_min
      }min after pickup and lasting ${excursion.duration_min}min.`
    );
  }
  if (type === 'temperature_nearing_limit' && range) {
    const margin = (config.margin_pct / 100) * (range.max - range.min);
    const around =
      excursion.direction === 'high'
        ? range.max - margin / 2
        : range.min + margin / 2;
    lines.push(
      `Readings stay inside the range, within ${config.margin_pct}% of it from the ${
        excursion.direction === 'high' ? 'maximum' : 'minimum'
      } (around ${formatMetricValue(around.toFixed(1), 'temperature')}), starting ${
        excursion.start_offset_min
      }min after pickup and lasting ${excursion.duration_min}min.`
    );
  }
  if (type === 'low_battery') {
    lines.push(
      `The battery drains from 100% and falls below ${config.battery_threshold_pct}% ${config.battery_low_offset_min}min after pickup.`
    );
  }
  if (type === 'shipment_not_moving') {
    lines.push(
      `The shipment stays in place for ${formatMinutes(excursion.duration_min)}, starting ${excursion.start_offset_min}min after pickup.`
    );
  }
  if (type === 'low_battery_at_origin') {
    lines.push(
      `The parcel is labelled but never picked up, and its battery sits below ${config.battery_threshold_pct}% at the origin.`
    );
  }
  if (type === 'pickup_overdue') {
    lines.push(
      `The parcel is labelled and a pickup is scheduled ${formatMinutes(pickupMinutes(config.journey_minutes))} after the start, but it is never picked up.`
    );
  }
  if (type === 'driver_cant_find_location') {
    lines.push(
      `The sensor sits on the destination for ${formatMinutes(excursion.duration_min)}, starting ${excursion.start_offset_min}min after pickup, and the parcel is never delivered.`
    );
  }
  if (type === 'incorrect_delivery_location') {
    lines.push(
      `The parcel is delivered, but the sensor's location readings over the final hour are about ${config.delivery_offset_ft} feet from the destination.`
    );
  }
  if (type === 'delivery_overdue') {
    lines.push(
      'Every scan arrives except Delivered, so the delivery passes its scheduled time with no delivered scan.'
    );
  }
  const hours = config.product?.viability_window;
  if (isViability(type) && hours) {
    const pickup = pickupMinutes(config.journey_minutes);
    const remaining = pickup + hours * 60 - config.started_minutes_ago;
    const arrival = config.journey_minutes - (pickup + hours * 60);
    lines.push(
      type === 'viability_at_risk_before_arrival'
        ? `The shipment is due ${formatMinutes(Math.abs(arrival))} ${arrival >= 0 ? 'after' : 'before'} the ${hours}h viability window ends.`
        : `The ${hours}h viability window ${
            remaining >= 0
              ? `ends about ${formatMinutes(remaining)} from now`
              : `ended about ${formatMinutes(-remaining)} ago`
          }, and the shipment is still on the road.`
    );
  }
  return lines;
};

/** The incident window as the create endpoint expects it. */
const windowPayload = (
  config: SimulationScenarioConfig
): Record<string, unknown> => ({
  start_offset_min: config.excursion.start_offset_min,
  duration_min: config.excursion.duration_min,
});

/** The `simulation_scenario` object the create endpoint expects. */
export const toScenarioPayload = (
  config: SimulationScenarioConfig
): Record<string, unknown> => {
  const type = config.scenario_type;
  const metric = scenarioMetric(type);
  const { excursion } = config;
  const base = {
    scenario_type: type,
    journey_minutes: config.journey_minutes,
    started_minutes_ago: config.started_minutes_ago,
    pace: effectivePace(config),
  };
  if (metric !== null && hasMagnitude(type)) {
    return {
      ...base,
      excursion: {
        direction: excursion.direction,
        [METRIC_INFO[metric].magnitudeKey]:
          excursion[METRIC_INFO[metric].magnitudeKey],
        ...windowPayload(config),
      },
    };
  }
  if (type === 'temperature_nearing_limit') {
    return {
      ...base,
      margin_pct: config.margin_pct,
      excursion: { direction: excursion.direction, ...windowPayload(config) },
    };
  }
  if (type === 'low_battery') {
    return {
      ...base,
      battery_threshold_pct: config.battery_threshold_pct,
      battery_low_offset_min: config.battery_low_offset_min,
    };
  }
  if (type === 'shipment_not_moving' || type === 'driver_cant_find_location') {
    return { ...base, excursion: windowPayload(config) };
  }
  if (type === 'low_battery_at_origin') {
    return { ...base, battery_threshold_pct: config.battery_threshold_pct };
  }
  if (type === 'incorrect_delivery_location') {
    return {
      ...base,
      delivery_offset_m: Math.round(
        config.delivery_offset_ft * METRES_PER_FOOT
      ),
    };
  }
  return base;
};

/**
 * The shipment's sensor rows with a simulator sensor guaranteed: unchanged when
 * one is already chosen, otherwise it fills the first blank row or is appended.
 * Its tracking ID is left empty for the drawer to generate.
 */
export const withSimulatorSensorRow = (
  rows: SensorFormRow[] | undefined,
  simulatorSensorId: string,
  isSimulatorSensor: (value: unknown) => boolean
): SensorFormRow[] => {
  const current = rows ?? [];
  if (current.some((row) => isSimulatorSensor(row?.sensor))) {
    return current;
  }
  const blank = current.findIndex(
    (row) => !row?.sensor && !row?.sensor_tracking_id
  );
  if (blank === -1) {
    return [...current, { sensor: simulatorSensorId }];
  }
  return current.map((row, index) =>
    index === blank ? { ...row, sensor: simulatorSensorId } : row
  );
};
